import { useState } from 'react'
import { ExternalLink } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  comparableCategoryLabel,
  formatEvidenceValue,
  titleize,
} from '@/components/deals/evidence/evidence-presentation'
import { MarketEvidencePendingProfiles } from '@/components/deals/evidence/MarketEvidencePendingProfiles'

const INITIAL_COMPARABLE_COUNT = 5

function comparableDate(observation) {
  return observation.observed_on || observation.observedOn || 'Date unavailable'
}

function comparableDistance(observation) {
  const distance = observation.distance_miles || observation.distanceMiles
  return distance ? `${distance} miles from postcode centre` : null
}

function comparableBedrooms(observation) {
  const value = observation.bedrooms
  if (value === null || value === undefined || value === '') return null

  const count = Number(value)
  if (!Number.isInteger(count) || count < 0) return null

  return `${count} ${count === 1 ? 'bedroom' : 'bedrooms'}`
}

export function MarketEvidenceComparables({
  groups,
  pendingProfileIds,
  profileLabels,
  profiles,
  unavailableProfileIds,
}) {
  const [expandedGroups, setExpandedGroups] = useState({})
  const displayedProfileKeys = groups.map((group) => group.profileKey)

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <section
          key={group.profileKey}
          aria-labelledby={`evidence-comparables-${group.profileId}`}
        >
          <h4
            id={`evidence-comparables-${group.profileId}`}
            className="mb-3 font-bold text-foreground"
          >
            {group.profileLabel}
          </h4>
          <div className="space-y-3">
            {group.categories.map((category) => {
              const observations = category.observations ?? []
              const groupKey = `${group.profileKey}:${category.category}`
              const categoryName = comparableCategoryLabel(category.category)
              const expanded = expandedGroups[groupKey] === true
              const visibleObservations = expanded
                ? observations
                : observations.slice(0, INITIAL_COMPARABLE_COUNT)

              return (
                <article key={groupKey} className="rounded-lg border border-border bg-background p-4">
                  <h5 className="font-semibold text-foreground">
                    {categoryName}
                  </h5>
                  {visibleObservations.length > 0 ? (
                    <ul className="mt-3 divide-y divide-border">
                      {visibleObservations.map((observation) => {
                        const address = observation.address || 'Address unavailable'
                        const propertyType = observation.property_type || observation.propertyType
                        const metadata = [
                          propertyType ? titleize(propertyType) : null,
                          comparableBedrooms(observation),
                          comparableDate(observation),
                          comparableDistance(observation),
                          observation.basis ? titleize(observation.basis) : null,
                        ].filter(Boolean)

                        return (
                          <li
                            key={observation.id}
                            className="grid gap-2 py-3 first:pt-0 last:pb-0 sm:grid-cols-[1fr_auto]"
                          >
                            <div>
                              <p className="font-medium text-foreground">{address}</p>
                              <p className="text-xs text-muted-foreground">{metadata.join(' · ')}</p>
                              {observation.source_url ? (
                                <a
                                  href={observation.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`View source for ${address}`}
                                  className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                                >
                                  View source
                                  <ExternalLink className="size-3.5" aria-hidden="true" />
                                </a>
                              ) : null}
                            </div>
                            <p className="font-semibold text-foreground">
                              {formatEvidenceValue(category.category, observation)}
                            </p>
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <p className="mt-3 text-sm text-muted-foreground">
                      No individual comparable properties supplied for this evidence category.
                    </p>
                  )}

                  {observations.length > INITIAL_COMPARABLE_COUNT ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-3"
                      aria-label={expanded
                        ? `View less ${categoryName} for ${group.profileLabel}`
                        : `Show all ${observations.length} ${categoryName} for ${group.profileLabel}`}
                      onClick={() => {
                        setExpandedGroups((current) => ({ ...current, [groupKey]: !expanded }))
                      }}
                    >
                      {expanded ? 'View less' : `Show all (${observations.length - INITIAL_COMPARABLE_COUNT} more)`}
                    </Button>
                  ) : null}
                </article>
              )
            })}
          </div>
        </section>
      ))}
      <MarketEvidencePendingProfiles
        hiddenProfileKeys={displayedProfileKeys}
        profileIds={pendingProfileIds}
        profileLabels={profileLabels}
        profiles={profiles}
      />
      <MarketEvidencePendingProfiles
        hiddenProfileKeys={displayedProfileKeys}
        profileIds={unavailableProfileIds}
        profileLabels={profileLabels}
        profiles={profiles}
        status="unavailable"
      />
    </div>
  )
}
